// «Aggiungi strumento»: prima il market (ricerca, categorie, tessere), poi la scheda della
// tipologia scelta, che si collega a modo suo. Memo: PopTown chiede l'abbinamento e mostra il codice,
// lo approvi in memo › Impostazioni › Chiavi. Server MCP: indirizzo dell'endpoint e chiave.
// Gmail: il client OAuth tuo, poi il consenso su Google, che torna alla pagina Strumenti.
// Pushover: token dell'applicazione e destinatari, verificati da PopTown.
// API: un servizio HTTP letto in GET e scritto se lo accendi, con l'autenticazione scelta (ApiForm).
// Strumenti del CLI: quale CLI; gli strumenti nativi si accendono poi nella scheda.
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import type { NewTool, PendingTool, PushRecipient, ToolKind } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Field, TextArea, TextField } from '../../components/Field';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { Select } from '../../components/Select';
import { ApiForm, MethodChips, apiReady, NO_AUTH, type ApiAuth } from './ApiForm';
import { ParamsField, paramsReady } from './ParamsField';
import { GmailForm, PushoverHelp, RecipientsEditor } from './ConnectForms';
import type { Credentials } from './ConnectForms';
import { ToolMarket } from './ToolMarket';

const POLL_MS = 3000;

const NAME_HINT: Record<string, string> = {
  memo: 'Come lo vedranno i Pops, es. memo-flow o memo-cortex.',
  mcp: 'Come lo vedranno i Pops, es. gitea o atlas.',
  gmail: 'Come lo vedranno i Pops, es. gmail-invio o gmail-personale.',
  pushover: 'Come lo vedranno i Pops, es. pushover-casa.',
  api: 'Come lo vedranno i Pops, es. meteo o gestionale.',
  cli: 'Come lo vedranno i Pops, es. claude-con-shell o codex-in-rete.',
};

const CLIS = [
  { value: 'claude-code', label: 'Claude Code' },
  { value: 'codex', label: 'Codex' },
  { value: 'antigravity', label: 'Antigravity' },
];

function message(e: unknown, fallback: string): string {
  return e instanceof ApiError && e.detail ? e.detail : fallback;
}

export function AddTool({
  kinds,
  linked,
  resume,
  onClose,
}: {
  kinds: ToolKind[];
  /** quante connessioni ci sono già, per tipologia */
  linked: Record<string, number>;
  resume?: PendingTool;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<ToolKind | null>(
    resume ? (kinds.find((k) => k.id === resume.kind) ?? null) : null,
  );
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [token, setToken] = useState('');
  const [google, setGoogle] = useState<Credentials>({ clientId: '', clientSecret: '' });
  const [people, setPeople] = useState<PushRecipient[]>([{ name: '', key: '' }]);
  const [auth, setAuth] = useState<ApiAuth>(NO_AUTH);
  const [maxBytes, setMaxBytes] = useState(1_000_000);
  const [params, setParams] = useState<string | null>(null);
  const [cli, setCli] = useState('claude-code');
  const [pending, setPending] = useState<PendingTool | null>(resume ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // finché l'abbinamento aspetta, si chiede a PopTown ogni 3 secondi se è stato approvato
  useEffect(() => {
    if (!pending || done) return undefined;
    const timer = window.setInterval(() => {
      api
        .pollTool(pending.id)
        .then((r) => {
          if (r.status === 'connected') setDone(true);
          else if (r.status === 'rejected' || r.status === 'expired') {
            setPending(null);
            setError(r.status === 'rejected' ? 'Richiesta rifiutata in memo.' : 'Scaduta.');
          }
        })
        .catch((e: unknown) => {
          setPending(null);
          setError(message(e, 'Abbinamento non riuscito.'));
        });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [pending, done]);

  const id = kind?.id ?? '';
  const body = (): NewTool => {
    const base = { kind: id, name, url, description };
    if (id === 'gmail')
      return {
        ...base,
        client_id: google.clientId,
        client_secret: google.clientSecret,
        origin: window.location.origin,
      };
    if (id === 'pushover')
      return { ...base, token, recipients: people.filter((r) => r.name.trim() || r.key.trim()) };
    if (id === 'api')
      return {
        ...base,
        token: auth.mode === 'none' ? '' : token,
        auth,
        max_bytes: maxBytes,
        params: params ?? '',
      };
    if (id === 'cli') return { ...base, cli };
    return { ...base, token };
  };

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.addTool(body());
      // Gmail: si va da Google, che riporta alla pagina Strumenti con l'esito
      if (r.authorize) window.location.assign(r.authorize);
      else if (r.pending) setPending(r.pending);
      else setDone(true);
    } catch (e) {
      setError(message(e, 'Non riesco a collegare lo strumento.'));
    } finally {
      setBusy(false);
    }
  };

  const ready =
    !!name.trim() &&
    (id === 'gmail'
      ? !!google.clientId.trim() && !!google.clientSecret.trim()
      : id === 'pushover'
        ? !!token.trim() && people.some((r) => r.name.trim() && r.key.trim())
        : id === 'api'
          ? apiReady(url, auth, token) && paramsReady(params)
          : id === 'cli'
            ? true
            : !!url.trim() && (id !== 'mcp' || !!token.trim()));

  const nameField = (
    <Field label="Nome" hint={NAME_HINT[id]}>
      <TextField value={name} onChange={setName} autoFocus />
    </Field>
  );

  const footer = done ? (
    <Button variant="primary" onClick={onClose}>
      Fatto
    </Button>
  ) : kind && !pending ? (
    <>
      <Button variant="secondary" onClick={onClose}>
        Annulla
      </Button>
      <Button
        variant="primary"
        icon={id === 'gmail' ? 'external-link' : undefined}
        loading={busy}
        disabled={!ready}
        onClick={() => void connect()}
      >
        {id === 'gmail' ? 'Collega con Google' : 'Collega'}
      </Button>
    </>
  ) : (
    <Button variant="secondary" onClick={onClose}>
      Chiudi
    </Button>
  );

  return (
    <Modal
      title={kind ? `Aggiungi ${kind.label}` : 'Aggiungi strumento'}
      width={kind ? 560 : 680}
      onClose={onClose}
      footer={footer}
    >
      {done ? (
        <Banner kind="info">
          Collegato. Scegli nella scheda dello strumento quali concedere ai Pops: di partenza sono
          attivi solo quelli di lettura.
        </Banner>
      ) : pending ? (
        <PairingCode pending={pending} />
      ) : !kind ? (
        <ToolMarket kinds={kinds} linked={linked} onPick={setKind} />
      ) : (
        <>
          <div>
            <Button size="sm" variant="ghost" icon="arrow-left" onClick={() => setKind(null)}>
              Tutti gli strumenti
            </Button>
          </div>
          {id === 'gmail' ? (
            <GmailForm value={google} onChange={setGoogle} nameField={nameField} />
          ) : (
            <>
              {id === 'pushover' && <PushoverHelp />}
              {nameField}
            </>
          )}
          {(id === 'memo' || id === 'mcp') && (
            <Field
              label="Indirizzo"
              hint={
                id === 'mcp'
                  ? "L'endpoint MCP completo, es. https://host/mcp/core/"
                  : "L'indirizzo dell'istanza, es. https://flow.sparknest.it"
              }
            >
              <TextField value={url} onChange={setUrl} mono />
            </Field>
          )}
          {(id === 'mcp' || id === 'pushover') && (
            <Field
              label={id === 'pushover' ? "API Token dell'applicazione" : 'Chiave'}
              hint="Resta sul server di PopTown: non torna mai alla plancia."
            >
              <TextField value={token} onChange={setToken} type="password" mono />
            </Field>
          )}
          {id === 'pushover' && <RecipientsEditor value={people} onChange={setPeople} />}
          {id === 'cli' && (
            <Field
              label="CLI"
              hint="Gli strumenti valgono per i pop che lavorano con questo CLI. Di partenza come oggi: shell e rete spente."
            >
              <Select label="CLI" value={cli} onChange={setCli} options={CLIS} width="100%" />
            </Field>
          )}
          {id === 'api' && (
            <ApiForm
              url={url}
              onUrl={(v) => {
                // l'avviso del tentativo precedente non vale più per l'indirizzo cambiato
                setUrl(v);
                setError(null);
              }}
              auth={auth}
              onAuth={setAuth}
              secret={token}
              onSecret={setToken}
              maxBytes={maxBytes}
              onMaxBytes={setMaxBytes}
            />
          )}
          <Field
            label="Descrizione"
            hint={
              id === 'api'
                ? 'A cosa serve: la legge il PM per scegliere chi usa lo strumento, e la vede il pop.'
                : 'A cosa serve: la legge il PM per decidere chi usa lo strumento.'
            }
          >
            <TextArea value={description} onChange={setDescription} rows={3} />
          </Field>
          {id === 'api' && (
            <>
              <ParamsField value={params} onChange={setParams} />
              <MethodChips />
            </>
          )}
        </>
      )}
      {error && <Banner kind="danger">{error}</Banner>}
    </Modal>
  );
}

function PairingCode({ pending }: { pending: PendingTool }) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        background: 'var(--panel-2)',
        padding: 14,
      }}
    >
      <div className="t-body" style={{ fontWeight: 600, marginBottom: 6, display: 'flex', gap: 8 }}>
        <Icon name="hourglass" size={18} />
        In attesa di approvazione
      </div>
      <div className="t-small dim" style={{ marginBottom: 10 }}>
        Apri memo › Impostazioni › Chiavi ({pending.url}), controlla il codice e approva la
        richiesta «PopTown».
      </div>
      <div
        style={{
          font: '700 26px/32px var(--font-mono)',
          letterSpacing: '.12em',
          textAlign: 'center',
        }}
      >
        {pending.userCode}
      </div>
    </div>
  );
}
